import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  CHART_COLORS,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_PROPS,
} from '../../lib/chart-theme.js';
import type {
  AggregatedBreakthroughRow,
  BreakthroughEvaluationDays,
} from '../../types/breakthrough.js';
import { GRANULARITY_LABELS, type Granularity } from '../../lib/kpi-aggregation.js';

/**
 * @graph-connects none
 */
interface BreakthroughChartProps {
  data: AggregatedBreakthroughRow[];
  evaluationDays: BreakthroughEvaluationDays;
  granularity?: Granularity;
}

/** @graph-connects none */
interface ChartDatum {
  label: string;
  cohortCount: number;
  breakthroughRateM1: number | null;
  breakthroughRateM2: number | null;
  breakthroughRateM3: number | null;
}

/**
 * 突破率系列の dataKey 接頭辞。tooltip でパーセント表示に切り替える判定に使う。
 * @graph-connects none
 */
const RATE_KEY_PREFIX = 'breakthroughRate';

/** @graph-connects none */
function formatRate(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

/** @graph-connects none */
function formatTooltipRate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 1〜3ヶ月目突破率のコホートチャート。棒グラフ（新規会員数）+ 折れ線3本（1/2/3ヶ月目突破率）の複合グラフ。粒度切替に対応。表示するのは評価期間を経過した確定値のみで、未経過の突破月は線が途切れる。
 * @graph-connects GRANULARITY_LABELS [references] 粒度表示ラベル定数
 */
export function BreakthroughChart({ data, evaluationDays, granularity }: BreakthroughChartProps) {
  const { t } = useTranslation();

  const heading = (
    <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
      {t('breakthrough.title')}
      {granularity ? `（${GRANULARITY_LABELS[granularity]}）` : ''}
      <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)]">
        {t('breakthrough.confirmedOnlyNote', {
          daysM1: evaluationDays.m1,
          daysM2: evaluationDays.m2,
          daysM3: evaluationDays.m3,
        })}
      </span>
    </h3>
  );

  if (data.length === 0) {
    return (
      <Card className="p-4">
        {heading}
        <p className="text-xs text-[var(--color-text-muted)]">{t('common.noData')}</p>
      </Card>
    );
  }

  const chartData: ChartDatum[] = data.map((row) => ({
    label: row.displayLabel,
    cohortCount: row.cohortCount,
    breakthroughRateM1: row.breakthroughRateM1,
    breakthroughRateM2: row.breakthroughRateM2,
    breakthroughRateM3: row.breakthroughRateM3,
  }));

  return (
    <Card className="p-4">
      {heading}
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
            <YAxis
              yAxisId="left"
              tick={AXIS_TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={AXIS_TICK_STYLE}
              tickLine={false}
              axisLine={false}
              domain={[0, 1]}
              tickFormatter={formatRate}
            />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              formatter={
                ((value: number | null, name: string, item: { dataKey?: string | number }) => {
                  if (value === null || value === undefined) return null;
                  const dataKey = typeof item.dataKey === 'string' ? item.dataKey : '';
                  if (dataKey.startsWith(RATE_KEY_PREFIX)) {
                    return [formatTooltipRate(value), name];
                  }
                  return [value.toLocaleString('ja-JP'), name];
                }) as never
              }
              itemSorter={() => 0}
            />
            <Legend {...LEGEND_PROPS} />
            <Bar
              yAxisId="left"
              dataKey="cohortCount"
              name={t('breakthrough.newMembers')}
              fill={CHART_COLORS[2]}
              radius={[2, 2, 0, 0]}
              barSize={24}
            />
            <Line
              yAxisId="right"
              dataKey="breakthroughRateM1"
              name={t('breakthrough.rateM1')}
              type="monotone"
              stroke={CHART_COLORS[0]}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_COLORS[0] }}
              connectNulls={false}
            />
            <Line
              yAxisId="right"
              dataKey="breakthroughRateM2"
              name={t('breakthrough.rateM2')}
              type="monotone"
              stroke={CHART_COLORS[1]}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_COLORS[1] }}
              connectNulls={false}
            />
            <Line
              yAxisId="right"
              dataKey="breakthroughRateM3"
              name={t('breakthrough.rateM3')}
              type="monotone"
              stroke={CHART_COLORS[3]}
              strokeWidth={2}
              dot={{ r: 3, fill: CHART_COLORS[3] }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
